import { CdnAsset } from "#components/ui/CdnAsset.tsx";
import { TextLoading } from "#components/ui/text-loading/TextLoading.tsx";
import { useFileMetadata } from "#features/conversations/hooks/useFileMetadata.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { FileUploadStatus } from "#types/types.ts";
import { Button, Div, Flex, Spinner, Text, type DivProps, type FlexProps, type TextProps } from "@hopper-ui/components";
import { DismissIcon, WarningIcon } from "@hopper-ui/icons";
import { useMemo, type ReactNode } from "react";

interface Props {
    file: FileModel;
    onRemove?: () => void;
}

// The view status the card actually renders. It's `FileUploadStatus` widened with `"Processing"`: a
// completed-but-not-yet-available file (`isProcessing`) reuses the "Uploading" container/icon styling
// (below) but gets its own subtitle, and a completed upload whose availability comes back `"Failed"`
// resolves to the `"Failed"` case too — distinguished from an upload failure by its own subtitle.
type CardStatus = FileUploadStatus | "Processing";

const containerStyle: Record<CardStatus, Partial<DivProps>> = {
    Completed: {
        backgroundColor: "transparent",
        border: "neutral-weakest"
    },
    Uploading: {
        backgroundColor: "transparent",
        border: "neutral-weakest"
    },
    Processing: {
        backgroundColor: "transparent",
        border: "neutral-weakest"
    },
    Failed: {
        backgroundColor: "transparent",
        border: "neutral-weakest"
    }
};

const iconStyle: Record<CardStatus, Partial<FlexProps>> = {
    Completed: {
        backgroundColor: "neutral-weakest"
    },
    Uploading: {
        backgroundColor: "core_orchid-bloom-200"
    },
    Processing: {
        backgroundColor: "core_orchid-bloom-200"
    },
    Failed: {
        backgroundColor: "danger-weak"
    }
};

const fileNameStyle: Record<CardStatus, Partial<TextProps>> = {
    Completed: {
        fontWeight: "body-xs-medium",
        color: "neutral"
    },
    Uploading: {
        fontStyle: "italic",
        color: "neutral-weak"
    },
    Processing: {
        fontStyle: "italic",
        color: "neutral-weak"
    },
    Failed: {
        fontStyle: "italic",
        color: "neutral-weak"
    }
};

export const FileCard = ({ file, onRemove }: Props) => {
    const { tShared } = useSharedTranslation();
    const { fileName, fileUploadStatus, availability, error, isProcessing } = file;
    const { getFileExtension, getFileTypeName, getFileTypeIcon } = useFileMetadata();

    // An indexation failure only exists once the upload itself completed; distinguish it from an
    // upload failure so the subtitle below can point at the right recourse (remove/re-upload vs. retry).
    const isIndexationFailure = fileUploadStatus === "Completed" && availability === "Failed";

    const cardStatus: CardStatus = isIndexationFailure
        ? "Failed"
        : isProcessing
            ? "Processing"
            : fileUploadStatus;

    const statusIcon: Record<CardStatus, ReactNode> = useMemo(() => ({
        Completed:
            <CdnAsset
                asset={getFileTypeIcon(fileName)}
                width="core_160"
                height="core_160"
                alt={getFileExtension(fileName)}
            />,
        Uploading: <Spinner aria-label={tShared("fileCard.uploading")} />,
        Processing: <Spinner aria-label={tShared("fileCard.processing")} />,
        Failed: <WarningIcon fill="danger-selected" size="sm" />
    }), [getFileTypeIcon, fileName, getFileExtension, tShared]);

    const fileProperties: Record<CardStatus, ReactNode> = useMemo(() => ({
        Completed: (
            <Text size="xs" color="neutral-weak">
                {getFileTypeName(fileName)}
            </Text>
        ),
        Uploading:   (
            <Text size="xs" color="neutral" fontWeight="body-xs-medium">
                <TextLoading delayMs={0} message={tShared("fileCard.uploading")} disableReveal />
            </Text>
        ),
        Processing: (
            <Text size="xs" color="neutral" fontWeight="body-xs-medium">
                <TextLoading delayMs={0} message={tShared("fileCard.processing")} disableReveal />
            </Text>
        ),
        Failed: (
            <Text size="xs" color="danger-selected">
                {isIndexationFailure ? tShared("fileCard.errors.indexationFailed") : (error ?? tShared("fileCard.errors.uploadFailed"))}
            </Text>
        )
    }), [error, fileName, getFileTypeName, isIndexationFailure, tShared]);

    return (
        <Div
            padding="inset-sm"
            borderRadius="rounded-lg"
            UNSAFE_maxWidth="var(--wai-file-card-max-width)"
            UNSAFE_minHeight="var(--wai-file-card-min-height)"
            {...containerStyle[cardStatus]}
        >
            <Flex
                alignItems="center"
                gap="inline-sm"
            >
                <Flex
                    width="core_320"
                    height="core_320"
                    borderRadius="rounded-md"
                    justifyContent="center"
                    alignItems="center"
                    shrink={0}
                    color="core_samoyed"
                    {...iconStyle[cardStatus]}
                >
                    {statusIcon[cardStatus]}
                </Flex>
                <Div>
                    <Div UNSAFE_lineHeight={1}>
                        <Text
                            size="xs"
                            paddingRight="inset-xs"
                            title={fileName}
                            className="wai-line-clamp"
                            {...fileNameStyle[cardStatus]}
                        >
                            {fileName}
                        </Text>
                    </Div>
                    <Div UNSAFE_lineHeight={1}>
                        {fileProperties[cardStatus]}
                    </Div>
                </Div>
                {
                    onRemove && (
                        <Div alignSelf="flex-start">
                            <Button
                                aria-label={tShared("fileCard.removeFile")}
                                size="xs"
                                variant="ghost-secondary"
                                borderRadius="circle"
                                backgroundColor="neutral-weakest"
                                backgroundColorHover="neutral-weak"
                                onPress={onRemove}
                            >
                                <DismissIcon fill="neutral-weak" />
                            </Button>
                        </Div>)
                }
            </Flex>
        </Div>
    );
};
